package com.flowyride.core.map

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.snapshotFlow
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Surface
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import kotlinx.coroutines.launch
import kotlin.math.abs
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import com.flowyride.core.designsystem.theme.FlowyColors
import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.map.resources.Res
import com.flowyride.core.map.resources.ic_speed_camera
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.first
import kotlinx.serialization.json.JsonPrimitive
import org.jetbrains.compose.resources.painterResource
import org.maplibre.compose.camera.CameraAnimation
import org.maplibre.compose.camera.CameraMoveReason
import org.maplibre.compose.camera.CameraPosition
import org.maplibre.compose.camera.CameraUpdate
import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.dsl.asBoolean
import org.maplibre.compose.expressions.dsl.asNumber
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.dsl.feature
import org.maplibre.compose.expressions.dsl.gt
import org.maplibre.compose.expressions.dsl.image
import org.maplibre.compose.expressions.dsl.lt
import org.maplibre.compose.expressions.dsl.not
import org.maplibre.compose.expressions.value.BooleanValue
import org.maplibre.compose.expressions.value.LineCap
import org.maplibre.compose.expressions.value.LineJoin
import org.maplibre.compose.interaction.MapInteractions
import org.maplibre.compose.layers.LineLayer
import org.maplibre.compose.layers.SymbolLayer
import org.maplibre.compose.map.MaplibreMap
import org.maplibre.compose.map.StyleLoadState
import org.maplibre.compose.map.rememberMapState
import org.maplibre.compose.overlay.ExpandingAttributionButton
import org.maplibre.compose.overlay.MaplibreLogo
import org.maplibre.compose.sources.GeoJsonData
import org.maplibre.compose.sources.GeoJsonSource
import org.maplibre.compose.sources.rememberGeoJsonSource
import org.maplibre.compose.style.BaseStyle
import org.maplibre.compose.util.DpPadding
import org.maplibre.compose.util.MaplibreComposable
import org.maplibre.spatialk.geojson.BoundingBox
import org.maplibre.spatialk.geojson.Position
import kotlin.time.Duration.Companion.milliseconds

/**
 * The map while riding: follows [position], turned towards [heading] and tilted (no 3D buildings),
 * the zoom following the speed, adjusted by the app's buttons ([zoomOffset]). The rider can also move it
 * by hand: [onMovedByHand] is called, and the camera stays put while [following] is false.
 *
 * @param doneIndex index in [track] up to which the ride is done (drawn grey).
 * @param connector way to the departure or back onto the ride, dashed.
 * @param overview shows the whole ride, north up and flat, instead of following the rider.
 * @param following the camera follows the rider (ignored in [overview]); turning it back on recentres at once.
 * @param northUpDescription accessibility label of the compass, shown while the map is the rider's and not north up.
 * @param obscuredInsets map area hidden by the app's panels: the rider's arrow sits in the rest,
 *   in its lower part so that the road ahead shows.
 */
@Composable
fun NavigationMap(
    track: List<GeoPoint>,
    doneIndex: Int,
    position: GeoPoint?,
    heading: Double?,
    speedMetersPerSecond: Double?,
    modifier: Modifier = Modifier,
    connector: List<GeoPoint>? = null,
    stops: List<GeoPoint> = emptyList(),
    speedCameras: List<GeoPoint> = emptyList(),
    zoomOffset: Double = 0.0,
    overview: Boolean = false,
    following: Boolean = true,
    onMovedByHand: () -> Unit = {},
    northUpDescription: String = "",
    obscuredInsets: PaddingValues = PaddingValues(0.dp),
) {
    // Tilting by hand is of no use while riding.
    val interactions = remember {
        MapInteractions {
            camera {
                tilt { enabled = false }
            }
        }
    }
    // Never the whole track at each fix: see NavigationTrack.
    val navigationTrack = remember(track) { NavigationTrack(track) }
    val chunks = remember(navigationTrack) { GeoJsonData.JsonString(navigationTrack.chunksGeoJson) }
    val around = remember(navigationTrack, doneIndex) { GeoJsonData.JsonString(navigationTrack.aroundGeoJson(doneIndex)) }
    val chunk = navigationTrack.chunkAt(doneIndex)
    val chunksDone = remember(chunk) { feature[NavigationTrack.CHUNK_PROPERTY].asNumber() lt const(chunk.toFloat()) }
    val chunksRemaining = remember(chunk) { feature[NavigationTrack.CHUNK_PROPERTY].asNumber() gt const(chunk.toFloat()) }
    val connectorGeoJson = remember(connector) { connector?.takeIf { it.size >= 2 }?.let(GeoJson::lineString) }
    val camerasGeoJson = remember(speedCameras) { speedCameras.takeIf { it.isNotEmpty() }?.let(GeoJson::points) }
    val cameraIcon = painterResource(Res.drawable.ic_speed_camera)

    val start = position ?: track.firstOrNull()
    val mapState = rememberMapState(
        baseStyle = BaseStyle.Uri(MAP_STYLE_URL),
        initialCameraPosition = CameraPosition(
            target = Position(longitude = start?.longitude ?: 2.5, latitude = start?.latitude ?: 46.6),
            zoom = if (start != null) BASE_ZOOM else 4.5,
            tilt = TILT,
        ),
    ) {
        LandscapeLayers()
        if (navigationTrack.chunkCount > 0) {
            val chunksSource = rememberGeoJsonSource(chunks)
            val aroundSource = rememberGeoJsonSource(around)
            // Every casing under every line: the chunks overlap, a casing must not cover the line of its neighbour.
            RideLine("nav-done-chunks", chunksSource, chunksDone, DoneColor, 6.dp)
            RideLine("nav-done", aroundSource, AroundDone, DoneColor, 6.dp)
            RideLine("nav-casing-chunks", chunksSource, chunksRemaining, FlowyColors.RouteCasing, 11.dp)
            RideLine("nav-casing", aroundSource, AroundRemaining, FlowyColors.RouteCasing, 11.dp)
            RideLine("nav-remaining-chunks", chunksSource, chunksRemaining, FlowyColors.RouteLine, 7.dp)
            RideLine("nav-remaining", aroundSource, AroundRemaining, FlowyColors.RouteLine, 7.dp)
        }
        if (connectorGeoJson != null) {
            LineLayer(
                id = "nav-connector",
                source = rememberGeoJsonSource(GeoJsonData.JsonString(connectorGeoJson)),
                color = const(FlowyColors.Stop),
                width = const(6.dp),
                dasharray = const(listOf(1.5f, 1f)),
                cap = const(LineCap.Butt),
                join = const(LineJoin.Round),
            )
        }
        if (camerasGeoJson != null) {
            SymbolLayer(
                id = "nav-speed-cameras",
                source = rememberGeoJsonSource(GeoJsonData.JsonString(camerasGeoJson)),
                iconImage = image(cameraIcon, size = DpSize(26.dp, 26.dp)),
                iconAllowOverlap = const(true),
            )
        }
    }

    // Tilted, buildings would stand up in 3D: useless to ride, and costly for the battery.
    LaunchedEffect(mapState) {
        snapshotFlow { mapState.style.loadState }.filter { it == StyleLoadState.Ready }.first()
        mapState.style.layers
            .filter { it.type == "fill-extrusion" }
            .forEach { it.asMutable?.setLayoutProperty("visibility", JsonPrimitive("none")) }
    }

    val layoutDirection = LocalLayoutDirection.current
    val currentInsets by rememberUpdatedState(obscuredInsets)
    val currentZoomOffset by rememberUpdatedState(zoomOffset)
    val currentSpeed by rememberUpdatedState(speedMetersPerSecond)
    val currentOverview by rememberUpdatedState(overview)
    val currentFollowing by rememberUpdatedState(following)
    val currentOnMovedByHand by rememberUpdatedState(onMovedByHand)
    // A gesture interrupts the follow animation: from then on the camera is the rider's.
    LaunchedEffect(mapState) {
        snapshotFlow { mapState.isCameraMoving && mapState.cameraMoveReason == CameraMoveReason.GESTURE }
            .filter { it }
            .collect { currentOnMovedByHand() }
    }
    // The whole ride, connector included, like on the planner.
    // Keyed on the insets too: turning the phone moves the panels, the camera follows at once.
    LaunchedEffect(mapState, overview, track, connector, obscuredInsets) {
        if (!overview) return@LaunchedEffect
        val bounds = (track + connector.orEmpty() + listOfNotNull(position)).bounds() ?: return@LaunchedEffect
        val insets = currentInsets
        mapState.animateCameraToBounds(
            boundingBox = BoundingBox(west = bounds.west, south = bounds.south, east = bounds.east, north = bounds.north),
            bearing = 0.0,
            tilt = 0.0,
            // The rider-low padding of the follow mode would shift the fit.
            cameraPadding = DpPadding.Zero,
            fitPadding = DpPadding(
                left = insets.calculateLeftPadding(layoutDirection) + OverviewMargin,
                top = insets.calculateTopPadding() + OverviewMargin,
                right = insets.calculateRightPadding(layoutDirection) + OverviewMargin,
                bottom = insets.calculateBottomPadding() + OverviewMargin,
            ),
            animation = CameraAnimation.Ease(duration = OVERVIEW_EASE),
        )
    }
    // The +/− buttons answer at once, without waiting for the next fix.
    LaunchedEffect(mapState, zoomOffset) {
        if (position == null || currentOverview || !currentFollowing) return@LaunchedEffect
        mapState.animateCamera(
            CameraUpdate(zoom = zoomFor(currentSpeed) + zoomOffset),
            CameraAnimation.Ease(duration = ZOOM_EASE),
        )
    }
    LaunchedEffect(mapState, position, heading, overview, following, obscuredInsets) {
        if (overview || !following) return@LaunchedEffect
        val target = position ?: return@LaunchedEffect
        val viewportHeight = mapState.viewport?.size?.height ?: 0.dp
        val insets = currentInsets
        val obscured = DpPadding(
            left = insets.calculateLeftPadding(layoutDirection),
            top = insets.calculateTopPadding(),
            right = insets.calculateRightPadding(layoutDirection),
            bottom = insets.calculateBottomPadding(),
        )
        mapState.animateCamera(
            CameraUpdate(
                target = Position(longitude = target.longitude, latitude = target.latitude),
                zoom = zoomFor(speedMetersPerSecond) + currentZoomOffset,
                bearing = heading?.let { (it + 360.0) % 360.0 },
                tilt = TILT,
                padding = followPadding(obscured, viewportHeight),
            ),
            CameraAnimation.Ease(duration = CAMERA_EASE, easing = org.maplibre.compose.camera.CubicBezier.Linear),
        )
    }

    val scope = rememberCoroutineScope()
    MaplibreMap(modifier = modifier, state = mapState, interactions = interactions) {
        Box(Modifier.matchParentSize().padding(obscuredInsets).padding(8.dp)) {
            MaplibreLogo(Modifier.align(Alignment.BottomStart))
            ExpandingAttributionButton(Modifier.align(Alignment.BottomEnd), contentAlignment = Alignment.BottomEnd)
            // Following, the map turns with the rider: north only matters once it is the rider's to move.
            val bearing = mapState.cameraPosition.bearing
            if ((overview || !following) && abs(normalizedBearing(bearing)) > NORTH_TOLERANCE_DEGREES) {
                CompassButton(
                    bearing = bearing,
                    description = northUpDescription,
                    onClick = {
                        scope.launch { mapState.animateCamera(CameraUpdate(bearing = 0.0), CameraAnimation.Ease(duration = ZOOM_EASE)) }
                    },
                    modifier = Modifier.align(Alignment.TopEnd).padding(top = 4.dp, end = 2.dp),
                )
            }
        }
        stops.forEach { stop ->
            Box(Modifier.placedAt(stop.toPosition()).size(14.dp)) {
                Canvas(Modifier.matchParentSize()) {
                    drawCircle(Color.White)
                    drawCircle(FlowyColors.Stop, radius = size.minDimension / 2 - 3.dp.toPx())
                }
            }
        }
        track.lastOrNull()?.let { arrival ->
            Box(Modifier.placedAt(arrival.toPosition()).size(18.dp)) {
                Canvas(Modifier.matchParentSize()) {
                    drawCircle(Color.White)
                    drawCircle(FlowyColors.Arrival, radius = size.minDimension / 2 - 3.dp.toPx())
                }
            }
        }
        if (position != null) {
            // The map turns with the rider: the arrow points up, give or take the camera's catching up.
            val rotation = ((heading ?: mapState.cameraPosition.bearing) - mapState.cameraPosition.bearing).toFloat()
            Box(Modifier.placedAt(position.toPosition()).size(ArrowSize)) {
                RiderArrow(Modifier.matchParentSize().rotate(rotation))
            }
        }
    }
}

@Composable
@MaplibreComposable
private fun RideLine(id: String, source: GeoJsonSource, filter: Expression<BooleanValue>, color: Color, width: Dp) {
    LineLayer(
        id = id,
        source = source,
        filter = filter,
        color = const(color),
        width = const(width),
        cap = const(LineCap.Round),
        join = const(LineJoin.Round),
    )
}

/** Round compass, its needle pointing north on the turned map; a tap turns the map north up. 64 dp: usable with gloves. */
@Composable
private fun CompassButton(bearing: Double, description: String, onClick: () -> Unit, modifier: Modifier = Modifier) {
    Surface(
        onClick = onClick,
        shape = CircleShape,
        color = Color.White,
        shadowElevation = 4.dp,
        modifier = modifier.size(64.dp).semantics { contentDescription = description },
    ) {
        Canvas(Modifier.padding(14.dp).rotate(-bearing.toFloat())) {
            val w = size.width
            val h = size.height
            val north = Path().apply {
                moveTo(w / 2, 0f)
                lineTo(w * 0.72f, h / 2)
                lineTo(w * 0.28f, h / 2)
                close()
            }
            val south = Path().apply {
                moveTo(w / 2, h)
                lineTo(w * 0.72f, h / 2)
                lineTo(w * 0.28f, h / 2)
                close()
            }
            drawPath(north, FlowyColors.Arrival)
            drawPath(south, Color(0xFF9E9E9E))
        }
    }
}

/** -180..180. */
private fun normalizedBearing(bearing: Double): Double = ((bearing % 360.0) + 540.0) % 360.0 - 180.0

@Composable
private fun RiderArrow(modifier: Modifier) {
    Canvas(modifier) {
        val w = size.width
        val h = size.height
        val arrow = Path().apply {
            moveTo(w / 2, h * 0.08f)
            lineTo(w * 0.88f, h * 0.9f)
            lineTo(w / 2, h * 0.7f)
            lineTo(w * 0.12f, h * 0.9f)
            close()
        }
        drawCircle(Color.Black.copy(alpha = 0.18f), radius = w * 0.5f, center = Offset(w / 2, h / 2 + 2))
        drawPath(arrow, Color.White, style = Stroke(width = 5.dp.toPx(), join = StrokeJoin.Round))
        drawPath(arrow, FlowyColors.Stop)
    }
}

/**
 * Camera padding while following. The focal point is the centre of the padded area: the rider sits in the middle
 * of the free width and, with part of the free height added on top, low on the screen (more road ahead).
 */
internal fun followPadding(obscured: DpPadding, viewportHeight: Dp): DpPadding {
    val free = (viewportHeight - obscured.top - obscured.bottom).coerceAtLeast(0.dp)
    return obscured.copy(top = obscured.top + free * RIDER_HEIGHT_SHARE)
}

/** Faster: less detail, further ahead. */
private fun zoomFor(speed: Double?): Double {
    val kmh = (speed ?: 0.0) * 3.6
    return when {
        kmh < 30 -> 17.0
        kmh < 60 -> 16.5
        kmh < 90 -> 16.0
        else -> 15.3
    }
}

private val DoneColor = Color(0xFF9E9E9E)
private val AroundDone = feature[NavigationTrack.DONE_PROPERTY].asBoolean()
private val AroundRemaining = !AroundDone
private val ArrowSize = 34.dp
private const val TILT = 50.0
private const val NORTH_TOLERANCE_DEGREES = 1.0
private const val BASE_ZOOM = 16.0

/** Share of the free height above the rider. */
private const val RIDER_HEIGHT_SHARE = 0.45f

/** About the time between two fixes: the camera glides from one to the next. */
private val CAMERA_EASE = 950.milliseconds

/** Zoom buttons: quick enough to feel immediate. */
private val ZOOM_EASE = 250.milliseconds
private val OVERVIEW_EASE = 600.milliseconds
private val OverviewMargin = 40.dp
